import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Loader2, Plus, Save, Trash2, X, Pencil, Bold, Italic, List as ListIcon,
  ListOrdered, Link as LinkIcon, Copy, Check, Download, Paperclip, Upload,
  Archive, FileText,
} from "lucide-react";
import {
  listStores, listTemplates, createTemplate, updateTemplate, deleteTemplate,
  uploadAttachments, deleteAttachment, renderTemplate, downloadAttachment,
  PLACEHOLDERS,
} from "../../../lib/emailTemplates";

const fmtBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const EmailTemplatesTab = () => {
  const [stores, setStores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [storeId, setStoreId] = useState("");
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([listStores(), listTemplates()]);
      setStores(s.filter((x) => !x.archived));
      setTemplates(t.items || []);
      setCategories(t.categories || []);
      if (!storeId && s.length) setStoreId(s.find((x) => !x.archived)?.id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const selected = useMemo(() => templates.find((t) => t.id === selectedId) || null, [templates, selectedId]);

  const grouped = useMemo(() => {
    const active = templates.filter((t) => !t.archived);
    const map = {};
    for (const t of active) {
      const c = t.category || "General";
      (map[c] = map[c] || []).push(t);
    }
    return map;
  }, [templates]);

  const handleSelectTemplate = (id) => { setSelectedId(id); setEditing(false); setCreating(false); };

  const handleCreate = () => {
    setSelectedId(null);
    setCreating(true);
    setEditing(true);
  };

  const handleSavedTemplate = async (saved, isNew) => {
    await reload();
    setSelectedId(saved.id);
    setEditing(false);
    setCreating(false);
    toast.success(isNew ? "Template created" : "Saved");
  };

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-email-templates-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Mail size={20} /> Email Templates
          </h2>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1">
            {templates.filter((t) => !t.archived).length} active across {categories.length} categor{categories.length === 1 ? "y" : "ies"}.
            {stores.length === 0 && " · Add a store profile first to render templates."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stores.length > 0 && (
            <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em]">
              Render with
              <select
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
                data-testid="template-store-selector"
              >
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          )}
          <button
            onClick={handleCreate}
            className="btn-brutal px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
            data-testid="new-template-btn"
          >
            <Plus size={14} /> New template
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
        {/* Left list */}
        <aside
          className="rounded-sm p-2 max-h-[70vh] overflow-y-auto"
          style={{ background: "#fff", border: "1px solid var(--ups-border)" }}
          data-testid="templates-list"
        >
          {Object.keys(grouped).length === 0 ? (
            <div className="p-4 text-[12px] text-[color:var(--ups-subtext)] text-center">
              No templates yet — click <strong>New template</strong> to add your first.
            </div>
          ) : (
            Object.keys(grouped).sort().map((cat) => (
              <div key={cat} className="mb-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] px-2 py-1 sticky top-0 bg-white">
                  {cat} <span className="opacity-60">· {grouped[cat].length}</span>
                </div>
                {grouped[cat].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleSelectTemplate(t.id)}
                    className={`w-full text-left px-2 py-2 rounded-sm text-[13px] ${selectedId === t.id ? "bg-[color:var(--ups-muted)]" : "hover:bg-[color:var(--ups-muted)]"}`}
                    style={selectedId === t.id ? { borderLeft: "3px solid var(--ups-gold)" } : {}}
                    data-testid={`template-row-${t.id}`}
                  >
                    <div className="font-bold truncate">{t.name}</div>
                    <div className="text-[11px] text-[color:var(--ups-subtext)] truncate">
                      {t.subject || "(no subject)"}
                    </div>
                    {(t.attachments || []).length > 0 && (
                      <div className="text-[10px] mt-0.5 flex items-center gap-1 text-[color:var(--ups-subtext)]">
                        <Paperclip size={9} /> {t.attachments.length}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </aside>

        {/* Right panel */}
        <section className="rounded-sm p-4 min-h-[60vh]" style={{ background: "#fff", border: "1px solid var(--ups-border)" }}>
          {creating || (selected && editing) ? (
            <TemplateEditor
              template={creating ? null : selected}
              categories={categories}
              onCancel={() => { setEditing(false); setCreating(false); if (creating) setSelectedId(null); }}
              onSaved={handleSavedTemplate}
            />
          ) : selected ? (
            <TemplateViewer
              template={selected}
              storeId={storeId}
              storeMissing={stores.length === 0}
              onEdit={() => setEditing(true)}
              onDeleted={async () => { setSelectedId(null); await reload(); }}
              onArchived={async () => { await reload(); }}
              onAttachmentsChanged={async () => { await reload(); }}
            />
          ) : (
            <div className="py-20 text-center text-[color:var(--ups-subtext)]" data-testid="template-empty-state">
              <FileText size={28} className="mx-auto mb-2 opacity-50" />
              Select a template from the left, or click <strong>New template</strong>.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

/* ============================================================ */
/* Viewer — read-only render with copy/download actions          */
/* ============================================================ */
const TemplateViewer = ({ template, storeId, storeMissing, onEdit, onDeleted, onArchived, onAttachmentsChanged }) => {
  const [rendered, setRendered] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setRendered(null);
    if (!storeId || storeMissing) return;
    let cancelled = false;
    setBusy(true);
    renderTemplate(template.id, storeId)
      .then((r) => { if (!cancelled) setRendered(r); })
      .catch((e) => { if (!cancelled) toast.error(e?.response?.data?.detail || "Render failed"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [template.id, storeId, storeMissing]);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — your browser blocked clipboard access");
    }
  };

  const copyHtml = async () => {
    if (!rendered?.body_html) return;
    try {
      const blob = new Blob([rendered.body_html], { type: "text/html" });
      const textBlob = new Blob([rendered.body_text || ""], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": blob, "text/plain": textBlob }),
      ]);
      setCopiedKey("body-html");
      setTimeout(() => setCopiedKey(null), 1800);
      toast.success("Copied — paste into Outlook with formatting");
    } catch (e) {
      // Fallback to plain text
      await copyText(rendered.body_text, "body-html");
    }
  };

  const handleArchive = async () => {
    try { await updateTemplate(template.id, { archived: !template.archived }); await onArchived(); }
    catch (e) { toast.error("Archive failed"); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${template.name}"? This also removes all attachments from storage.`)) return;
    try { await deleteTemplate(template.id); toast.success("Deleted"); await onDeleted(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const handleUploadAttachments = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await uploadAttachments(template.id, files);
      toast.success(`Added ${res.added} attachment${res.added === 1 ? "" : "s"}`);
      await onAttachmentsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const handleRemoveAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.filename}"?`)) return;
    try { await deleteAttachment(template.id, att.id); await onAttachmentsChanged(); }
    catch (e) { toast.error("Remove failed"); }
  };

  const handleDownload = async (att) => {
    try { await downloadAttachment(template.id, att); }
    catch (e) { toast.error(e?.message || "Download failed"); }
  };

  return (
    <div className="space-y-3" data-testid="template-viewer">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)]">{template.category || "General"}</div>
          <h3 className="font-display text-2xl font-bold">{template.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1.5" style={{ border: "1px solid var(--ups-border)" }} data-testid="edit-template-btn">
            <Pencil size={12} /> Edit
          </button>
          <button onClick={handleArchive} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)] flex items-center gap-1.5" style={{ border: "1px solid var(--ups-border)" }}>
            <Archive size={12} /> {template.archived ? "Unarchive" : "Archive"}
          </button>
          <button onClick={handleDelete} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-red-50 text-red-700 flex items-center gap-1.5" style={{ border: "1px solid #FCA5A5" }}>
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>

      {storeMissing && (
        <div className="p-3 rounded-sm text-[12px]" style={{ background: "#FEF3C7", border: "1px solid #F59E0B" }}>
          ⚠ Add a <strong>Store Profile</strong> first so this template can render with a signature.
        </div>
      )}

      {busy && <div className="py-4 flex justify-center"><Loader2 className="animate-spin" /></div>}

      {rendered && (
        <>
          {/* Subject */}
          <div className="rounded-sm p-3" style={{ border: "1px solid var(--ups-border)" }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Subject</span>
              <button onClick={() => copyText(rendered.subject, "subject")} className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline" data-testid="copy-subject-btn">
                {copiedKey === "subject" ? <Check size={11} /> : <Copy size={11} />} Copy
              </button>
            </div>
            <div className="text-[14px] font-medium" data-testid="rendered-subject">{rendered.subject || <em className="text-[color:var(--ups-subtext)]">(no subject)</em>}</div>
          </div>

          {/* Body */}
          <div className="rounded-sm" style={{ border: "1px solid var(--ups-border)" }}>
            <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--ups-muted)", borderBottom: "1px solid var(--ups-border)" }}>
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Body — rendered for {rendered.store_name}</span>
              <div className="flex items-center gap-2">
                <button onClick={copyHtml} className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline" data-testid="copy-body-html-btn">
                  {copiedKey === "body-html" ? <Check size={11} /> : <Copy size={11} />} Copy (rich)
                </button>
                <button onClick={() => copyText(rendered.body_text, "body-text")} className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline" data-testid="copy-body-text-btn">
                  {copiedKey === "body-text" ? <Check size={11} /> : <Copy size={11} />} Copy (plain)
                </button>
              </div>
            </div>
            <div
              className="p-4 max-w-none text-[14px] leading-relaxed email-rich-body"
              style={{ minHeight: 180 }}
              dangerouslySetInnerHTML={{ __html: rendered.body_html || "<em>(empty)</em>" }}
              data-testid="rendered-body"
            />
          </div>

          {/* Attachments */}
          <div className="rounded-sm p-3" style={{ border: "1px solid var(--ups-border)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">
                Attachments {(rendered.attachments || []).length > 0 && `· ${rendered.attachments.length}`}
              </span>
              <label className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline cursor-pointer" data-testid="upload-attachment-label">
                {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Add files
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { handleUploadAttachments(Array.from(e.target.files || [])); e.target.value = ""; }}
                  disabled={uploading}
                  data-testid="attachment-upload-input"
                />
              </label>
            </div>
            {(rendered.attachments || []).length === 0 ? (
              <div className="text-[12px] text-[color:var(--ups-subtext)] py-2">No attachments yet.</div>
            ) : (
              <div className="space-y-1">
                {rendered.attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center gap-2 p-2 rounded-sm"
                    style={{ background: "#FAFAF9", border: "1px solid var(--ups-border)" }}
                    data-testid={`attachment-${att.id}`}
                  >
                    <Paperclip size={14} className="opacity-60 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{att.filename}</div>
                      <div className="text-[10px] text-[color:var(--ups-subtext)] font-mono">{fmtBytes(att.byte_size)}</div>
                    </div>
                    <button
                      onClick={() => handleDownload(att)}
                      className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]"
                      title="Download"
                      data-testid={`download-attachment-${att.id}`}
                    >
                      <Download size={14} />
                    </button>
                    <button onClick={() => handleRemoveAttachment(att)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-700" title="Remove">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ============================================================ */
/* Editor — create / edit template metadata + body                */
/* ============================================================ */
const TemplateEditor = ({ template, categories, onCancel, onSaved }) => {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [category, setCategory] = useState(template?.category || categories[0] || "General");
  const [categoryMode, setCategoryMode] = useState("pick"); // 'pick' | 'new'
  const [newCategory, setNewCategory] = useState("");
  const [subject, setSubject] = useState(template?.subject || "");
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || "");
  const [busy, setBusy] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== bodyHtml) {
      editorRef.current.innerHTML = bodyHtml || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  const exec = (cmd, value = null) => {
    // execCommand requires focus inside the contentEditable target.
    editorRef.current?.focus();
    document.execCommand(cmd, false, value);
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
  };

  const insertPlaceholder = (key) => {
    editorRef.current?.focus();
    document.execCommand("insertText", false, `{{${key}}}`);
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
  };

  const insertLink = () => {
    const url = window.prompt("URL?", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    const finalCategory = categoryMode === "new" ? (newCategory.trim() || "General") : (category || "General");
    setBusy(true);
    try {
      const payload = { name: name.trim(), category: finalCategory, subject, body_html: bodyHtml };
      const saved = isNew ? await createTemplate(payload) : await updateTemplate(template.id, payload);
      onSaved(saved, isNew);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="template-editor">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-xl font-bold">{isNew ? "New template" : "Edit template"}</h3>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={busy} className="btn-brutal px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="save-template-btn">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Name *</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cold outreach — Restaurants"
            className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
            data-testid="template-name-input"
          />
        </label>
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Category</span>
          <div className="flex gap-2 mt-0.5">
            {categoryMode === "pick" ? (
              <>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="input-swiss flex-1 px-2 py-1.5 rounded-sm text-[13px]"
                  data-testid="template-category-select"
                >
                  {(categories.includes("General") ? categories : [...categories, "General"]).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setCategoryMode("new")}
                  className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
                  style={{ border: "1px solid var(--ups-border)" }}
                  data-testid="template-new-category-btn"
                >
                  + New
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  autoFocus
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="New category name"
                  className="input-swiss flex-1 px-2 py-1.5 rounded-sm text-[13px]"
                  data-testid="template-new-category-input"
                />
                <button
                  type="button"
                  onClick={() => { setCategoryMode("pick"); setNewCategory(""); }}
                  className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]"
                  style={{ border: "1px solid var(--ups-border)" }}
                >
                  ← Pick
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Subject</span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Print services from {{store_name}}"
          className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
          data-testid="template-subject-input"
        />
      </label>

      <div>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Body</span>
          <span className="text-[10px] text-[color:var(--ups-subtext)]">
            Tip: drop placeholders below into the body where the store info should appear.
          </span>
        </div>
        {/* Toolbar */}
        <div className="flex items-center gap-1 px-2 py-1.5 rounded-t-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)", borderBottom: "none" }}>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("bold"); }} className="p-1.5 rounded-sm hover:bg-white" title="Bold" data-testid="editor-bold"><Bold size={13} /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("italic"); }} className="p-1.5 rounded-sm hover:bg-white" title="Italic" data-testid="editor-italic"><Italic size={13} /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertUnorderedList"); }} className="p-1.5 rounded-sm hover:bg-white" title="Bulleted list"><ListIcon size={13} /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); exec("insertOrderedList"); }} className="p-1.5 rounded-sm hover:bg-white" title="Numbered list"><ListOrdered size={13} /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); insertLink(); }} className="p-1.5 rounded-sm hover:bg-white" title="Insert link"><LinkIcon size={13} /></button>
          <span className="mx-1 text-[color:var(--ups-border)]">·</span>
          <div className="flex items-center gap-1 flex-wrap">
            {PLACEHOLDERS.map((p) => (
              <button
                key={p.key}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertPlaceholder(p.key); }}
                className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm hover:bg-white"
                style={{ border: "1px solid var(--ups-border)" }}
                title={p.label}
                data-testid={`placeholder-${p.key}`}
              >
                {`{{${p.key}}}`}
              </button>
            ))}
          </div>
        </div>
        <div
          ref={editorRef}
          contentEditable
          onInput={(e) => setBodyHtml(e.currentTarget.innerHTML)}
          className="p-3 min-h-[260px] rounded-b-sm focus:outline-none text-[14px] leading-relaxed email-rich-body"
          style={{ background: "#fff", border: "1px solid var(--ups-border)" }}
          data-testid="template-body-editor"
          suppressContentEditableWarning
        />
      </div>
    </div>
  );
};

export default EmailTemplatesTab;
